import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import Header from './components/Header6';
import Footer from './components/Footer6';

const Template6Shop = () => {
    const navigate = useNavigate();
    const [activeFilter, setActiveFilter] = useState('All');
    const [sortBy, setSortBy] = useState('featured');
    const [currentPage, setCurrentPage] = useState(1);
    const [wishlist, setWishlist] = useState({});
    const [hoveredCard, setHoveredCard] = useState(null);

    const categories = ['All', 'Sarees', 'Kurtas', 'Ethnic Sets', 'Block Print', 'Dupattas'];

    const sortOptions = [
        { value: 'featured', label: 'Featured' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Newest First' },
    ];

    const products = [
        { id: 1, name: 'Chanderi Silk Saree', category: 'Sarees', price: 4999, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 2, name: 'Khadi Cotton Kurta', category: 'Kurtas', price: 2499, image: 'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 3, name: 'Jamdani Weave Dupatta', category: 'Dupattas', price: 1899, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 4, name: 'Block Print Anarkali', category: 'Block Print', price: 3999, image: 'https://images.pexels.com/photos/4614070/pexels-photo-4614070.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 5, name: 'Tussar Silk Ensemble', category: 'Ethnic Sets', price: 7499, image: 'https://images.pexels.com/photos/6712360/pexels-photo-6712360.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 6, name: 'Ikat Print Palazzo Set', category: 'Ethnic Sets', price: 2999, image: 'https://images.pexels.com/photos/7275385/pexels-photo-7275385.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 7, name: 'Banarasi Silk Saree', category: 'Sarees', price: 12999, image: 'https://images.pexels.com/photos/8953474/pexels-photo-8953474.jpeg?auto=compress&cs=tinysrgb&w=400' },
        { id: 8, name: 'Ajrakh Hand Block Kurta', category: 'Kurtas', price: 999, image: 'https://images.pexels.com/photos/2955375/pexels-photo-2955375.jpeg?auto=compress&cs=tinysrgb&w=400' },
    ];

    const filteredProducts = activeFilter === 'All' ? products : products.filter(p => p.category === activeFilter);

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        if (sortBy === 'price-low') return a.price - b.price;
        if (sortBy === 'price-high') return b.price - a.price;
        return 0;
    });

    const toggleWishlist = (e, productId) => {
        e.stopPropagation();
        setWishlist(prev => ({ ...prev, [productId]: !prev[productId] }));
    };

    const totalPages = 3;

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>

            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                <main className="pt-[100px] md:pt-[120px] pb-16">
                    {/* Page Header */}
                    <div className="text-center px-5 mb-10 lg:mb-14">
                        <p className="text-xs font-semibold tracking-[3px] uppercase mb-3" style={{ color: '#8B4513' }}>Curated For You</p>
                        <h1 style={{ fontFamily: "'Noto Serif Display', Georgia, serif" }} className="text-3xl lg:text-5xl font-normal text-[#2C1810] tracking-wide mb-3">
                            Our Collection
                        </h1>
                        <p className="text-[#7C6B5D] text-sm lg:text-base max-w-lg mx-auto" style={{ fontWeight: 300 }}>
                            Discover authentic handloom textiles and ethnic wear crafted by master artisans
                        </p>
                    </div>

                    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
                        {/* Filters Bar */}
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 pb-6" style={{ borderBottom: '1px solid #E8DFD1' }}>
                            {/* Category Pills */}
                            <div className="flex flex-wrap gap-2">
                                {categories.map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => { setActiveFilter(cat); setCurrentPage(1); }}
                                        style={{
                                            padding: '8px 20px',
                                            borderRadius: '2px',
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            letterSpacing: '1px',
                                            textTransform: 'uppercase',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontFamily: "'Nunito', sans-serif",
                                            backgroundColor: activeFilter === cat ? '#8B4513' : 'transparent',
                                            color: activeFilter === cat ? '#FFFFFF' : '#7C6B5D',
                                            border: activeFilter === cat ? '1px solid #8B4513' : '1px solid #D4C5B0',
                                        }}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>

                            {/* Sort */}
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                style={{
                                    padding: '10px 16px',
                                    backgroundColor: '#FAF7F2',
                                    border: '1px solid #D4C5B0',
                                    borderRadius: '2px',
                                    color: '#2C1810',
                                    fontFamily: "'Nunito', sans-serif",
                                    fontSize: '13px',
                                    outline: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                {sortOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Product Count */}
                        <p className="text-sm mb-6" style={{ color: '#7C6B5D', fontWeight: 300 }}>
                            Showing {sortedProducts.length} {sortedProducts.length === 1 ? 'product' : 'products'}
                        </p>

                        {/* Products Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                            {sortedProducts.map(product => (
                                <div
                                    key={product.id}
                                    onClick={() => navigate(`/template6/product/${product.id}`)}
                                    onMouseEnter={() => setHoveredCard(product.id)}
                                    onMouseLeave={() => setHoveredCard(null)}
                                    style={{
                                        backgroundColor: '#FAF7F2',
                                        border: `1px solid ${hoveredCard === product.id ? '#8B4513' : '#E8DFD1'}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        overflow: 'hidden',
                                        borderRadius: '4px',
                                        boxShadow: hoveredCard === product.id ? '0 8px 24px rgba(139, 69, 19, 0.12)' : 'none',
                                    }}
                                >
                                    <div style={{ position: 'relative', aspectRatio: '3/4', overflow: 'hidden' }}>
                                        <img
                                            src={product.image}
                                            alt={product.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transform: hoveredCard === product.id ? 'scale(1.05)' : 'scale(1)',
                                                transition: 'transform 0.5s ease',
                                            }}
                                        />
                                        <button
                                            onClick={(e) => toggleWishlist(e, product.id)}
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '10px',
                                                width: '32px',
                                                height: '32px',
                                                backgroundColor: 'rgba(250, 247, 242, 0.85)',
                                                border: 'none',
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                backdropFilter: 'blur(4px)',
                                            }}
                                        >
                                            <Heart size={16} style={{ color: wishlist[product.id] ? '#8B4513' : '#7C6B5D', fill: wishlist[product.id] ? '#8B4513' : 'none', transition: 'all 0.3s' }} />
                                        </button>
                                    </div>
                                    <div style={{ padding: '14px' }}>
                                        <h3 style={{ fontFamily: "'Nunito', sans-serif", fontSize: '13px', fontWeight: 600, color: '#2C1810', margin: '0 0 6px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {product.name}
                                        </h3>
                                        <span style={{ fontFamily: "'Nunito', sans-serif", fontSize: '15px', fontWeight: 600, color: '#8B4513' }}>
                                            {`\u20B9${product.price.toLocaleString('en-IN')}`}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <div className="flex justify-center items-center gap-2 mt-12">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                style={{
                                    width: '40px', height: '40px', border: '1px solid #D4C5B0', borderRadius: '2px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: 'transparent', cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === 1 ? 0.4 : 1, color: '#8B4513',
                                }}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            {[...Array(totalPages)].map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentPage(i + 1)}
                                    style={{
                                        width: '40px', height: '40px', borderRadius: '2px',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontFamily: "'Nunito', sans-serif", fontSize: '14px', fontWeight: 600,
                                        cursor: 'pointer', transition: 'all 0.3s',
                                        backgroundColor: currentPage === i + 1 ? '#8B4513' : 'transparent',
                                        color: currentPage === i + 1 ? '#FFFFFF' : '#7C6B5D',
                                        border: currentPage === i + 1 ? '1px solid #8B4513' : '1px solid #D4C5B0',
                                    }}
                                >
                                    {i + 1}
                                </button>
                            ))}
                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                    width: '40px', height: '40px', border: '1px solid #D4C5B0', borderRadius: '2px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: 'transparent', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    opacity: currentPage === totalPages ? 0.4 : 1, color: '#8B4513',
                                }}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Template6Shop;
